import React from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import { X, Network } from "lucide-react";
import { createPortal } from "react-dom";
import { AuthenticateSelect } from "../../auth/select";
import { RootState } from "../../../redux/store";
import { setProjectData } from "../../../redux/reducers/project";
import { webURL } from "../../../services/API";
import { Main } from "./style";
import { GlobalGoogleSpreadsheetPicker } from "./globalGoogleSpreadsheet";
import { applySpreadsheetToNodeList } from "./spreadsheetUtils";
import { PopupOverlay } from "../popupOverlay";

// Maps node action → the connection-type key used in globalConnections
export const ACTION_TO_TYPE: Record<string, string> = {
   shopify: 'shopify',        shopifyTrigger: 'shopify',
   whatsappSendMessage: 'whatsapp', whatsappListener: 'whatsapp',
   whatsappCloudSendMessage: 'whatsappCloud', whatsappCloudTrigger: 'whatsappCloud',
   airtable: 'airtable',     slack: 'slack', teams: 'microsoft', sharepoint: 'microsoft',sharepointTrigger: 'microsoft',
   gmail: 'google',           googleCalendar: 'google',
   oneDrive: 'microsoft',oneDriveTrigger: 'microsoft',
   googleDrive: 'google',     googleDriveTrigger: 'google',
   googleCalendarTrigger: 'google', spreadsheet: 'google', bigQuery: 'google',
   googleGemini: 'gemini',    gemini: 'gemini',
   notion: 'notion',          trello: 'trello',
   clickup: 'clickup',        jira: 'jira',
   openai: 'openai',
   deepseek: 'deepseek',      qwen: 'qwen',
   odoo: 'odoo',              odooTrigger: 'odoo',
   erpnext: 'erpnext',
   woocommerce: 'woocommerce', woocommerceTrigger: 'woocommerce',
   supabase: 'supabase',
   sql: 'sql',                sqlTrigger: 'sql',
   bosta: 'bosta',            bostaTrigger: 'bosta',
   easyOrders: 'easyOrders',  easyOrdersTrigger: 'easyOrders',
   outlook: 'microsoft',      outlookTrigger: 'microsoft',
   instagramSend: 'instagramMsgs', instaSendMessage: 'instagramMsgs',
   instagramPosting: 'instagramPosting',
   twilioSendSms: 'twilio',   imapTrigger: 'imap',
   wordpress: 'wordpress',    wordPress: 'wordpress',
};

const TYPE_LABELS: Record<string, string> = {
   shopify: 'Shopify',            whatsapp: 'WhatsApp',
   whatsappCloud: 'WhatsApp Cloud', airtable: 'Airtable',
   slack: 'Slack',                google: 'Google (Gmail / Calendar / Drive)',
   notion: 'Notion',              trello: 'Trello',
   clickup: 'ClickUp',            jira: 'Jira',
   openai: 'OpenAI',              gemini: 'Google Gemini',
   deepseek: 'DeepSeek',          qwen: 'Qwen',
   odoo: 'Odoo',                  erpnext: 'ERPNext',
   woocommerce: 'WooCommerce',    supabase: 'Supabase',
   sql: 'SQL',                    bosta: 'Bosta',
   easyOrders: 'EasyOrders',      microsoft: 'Microsoft',
   instagramMsgs: 'Instagram Messages', instagramPosting: 'Instagram Posting',
   twilio: 'Twilio',              imap: 'IMAP Email',
   wordpress: 'WordPress',
};

export function GlobalConnectionsPanel(
   { project, setProject, handleClose, nodeActions }:
   { project: projectType, setProject: React.Dispatch<React.SetStateAction<projectType | null>>, handleClose: () => void, nodeActions: string[] }
) {
   const userToken = useSelector((state: RootState) => state.user.data?.token);
   const dispatch = useDispatch();
   const { getNodes, getEdges, setNodes } = useReactFlow();

   const connectionTypes = React.useMemo(() => {
      const seen = new Set<string>();
      const result: Array<{ type: string, label: string }> = [];
      for (const action of nodeActions) {
         const type = ACTION_TO_TYPE[action];
         if (type && !seen.has(type)) {
            seen.add(type);
            result.push({ type, label: TYPE_LABELS[type] || type });
         }
      }
      return result;
   }, [nodeActions]);

   const hasSpreadsheetNodes = React.useMemo(
      () => nodeActions.includes('spreadsheet'),
      [nodeActions]
   );

   // Provider-metadata caches that every connection-dependent node keeps locally
   // (bases/tables for Airtable, files for Spreadsheet, channels for Slack, etc.).
   // Cleared whenever a node's connection actually changes so each node's own
   // "fetch metadata if not loaded yet" effect re-triggers instead of keeping
   // stale data from the previous connection.
   const METADATA_CACHE_KEYS = { bases: [], tables: [], records: [], fields: {}, files: [], channels: [] };

   // Push connection data directly into every canvas node of the matching type
   // that doesn't already have a custom (non-global) override, then let each
   // node's own effect (the same logic that runs when its panel is opened
   // manually) fetch bases/tables/fields/files/etc. and fully initialize.
   const applyToNodes = (connectionType: string, connectionData: any | null) => {
      setNodes(nodes => nodes.map(node => {
         if (ACTION_TO_TYPE[node.data?.action as string] !== connectionType) return node;
         const currentConn = node.data?.value?.connection;
         // Node has a manually-chosen connection (isGlobal not set) → leave it alone
         if (currentConn?.id && !currentConn?.isGlobal) return node;
         if (!connectionData) {
            // Global was removed → clear back to null, drop any cached metadata too
            return { ...node, data: { ...node.data, value: { ...node.data?.value, ...METADATA_CACHE_KEYS, connection: null, connectionId: null } } };
         }
         const isNewConnection = currentConn?.id !== connectionData.id;
         // Inject the real connection, tagged with isGlobal so future global changes apply
         return {
            ...node,
            data: {
               ...node.data,
               value: {
                  ...node.data?.value,
                  ...(isNewConnection ? METADATA_CACHE_KEYS : {}),
                  connection: { ...connectionData, isGlobal: true, connectionType },
                  connectionId: connectionData.id, // always set — some node panels read connectionId
               }
            }
         };
      }));
   };

   const saveGlobalConnections = (update: { [key: string]: string }) => {
      axios.put(`${webURL}/projects`, { isWorkflow: true, id: project._id, globalConnections: update }, {
         headers: { 'x-auth-token': userToken }
      });
      setProject(p => p ? { ...p, globalConnections: update } : p);
      dispatch(setProjectData({ globalConnections: update }));
   };

   const saveGlobalSpreadsheet = (update: { spreadsheet_id: string, spreadsheet_name?: string } | null) => {
      axios.put(`${webURL}/projects`, { isWorkflow: true, id: project._id, globalGoogleSpreadsheet: update }, {
         headers: { 'x-auth-token': userToken }
      });
      setProject(p => p ? { ...p, globalGoogleSpreadsheet: update || undefined } : p);
      dispatch(setProjectData({ globalGoogleSpreadsheet: update || undefined }));
   };

   const handleGlobalSpreadsheetSaved = (update: { spreadsheet_id: string, spreadsheet_name?: string } | null) => {
      saveGlobalSpreadsheet(update);
      const patchedNodes = applySpreadsheetToNodeList(getNodes(), update);
      setNodes(patchedNodes);
      axios.put(`${webURL}/projects`, {
         isWorkflow: true,
         id: project._id,
         globalGoogleSpreadsheet: update,
         nodes: patchedNodes,
         edges: getEdges(),
      }, { headers: { 'x-auth-token': userToken } });
   };

   const handleAuthenticate = (connectionType: string, action: 'add' | 'remove', data?: any) => {
      const current = project.globalConnections || {};
      if (action === 'add' && data?.id) {
         saveGlobalConnections({ ...current, [connectionType]: data.id });
         applyToNodes(connectionType, data);
      } else if (action === 'remove') {
         const updated = { ...current };
         delete updated[connectionType];
         saveGlobalConnections(updated);
         applyToNodes(connectionType, null);
         if (connectionType === 'google' && project.globalGoogleSpreadsheet) {
            saveGlobalSpreadsheet(null);
            const patchedNodes = applySpreadsheetToNodeList(getNodes(), null);
            setNodes(patchedNodes);
         }
      }
   };

   const portalTarget = typeof document !== "undefined" ? document.body : null;
   if (!portalTarget) return null;

   return createPortal(
      <PopupOverlay onClick={handleClose}>
         <Main onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={handleClose} aria-label="Close">
               <X size={18} />
            </button>
            <div className="panel-head">
               <div className="icon"><Network size={20} /></div>
               <div className="copy">
                  <h3>Global Connections</h3>
                  <p>Default integrations for nodes in this workflow.</p>
               </div>
            </div>
            <div className="content">
               <p className="tip">
                  Set a default connection per integration. Nodes will automatically use it unless you override per-node.
               </p>
               {connectionTypes.length === 0 ? (
                  <div className="empty">No integration nodes with connections found in this workflow.</div>
               ) : (
                  <div className="list">
                     {connectionTypes.map(({ type, label }) => (
                        <div key={type} className="item">
                           <div className="label">{label}</div>
                           <AuthenticateSelect
                              type={type}
                              defaultId={project.globalConnections?.[type] || ''}
                              handleAuthenticate={(action, data) => handleAuthenticate(type, action, data)}
                              withNoneOption
                              background="#4F3FE0"
                           />
                           {type === 'google' && hasSpreadsheetNodes ? (
                              <GlobalGoogleSpreadsheetPicker
                                 project={project}
                                 googleConnectionId={project.globalConnections?.google || ''}
                                 onSaved={handleGlobalSpreadsheetSaved}
                              />
                           ) : null}
                        </div>
                     ))}
                  </div>
               )}
               <div className="action">
                  <button onClick={handleClose}>Close</button>
               </div>
            </div>
         </Main>
      </PopupOverlay>,
      portalTarget
   );
}
